import { useRef, useState } from "react";

import { Button } from "src/base-components/Button";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import {
  useCreateScimToken,
  useGetScimTokens,
} from "src/scimToken/api/queries";
import { errorMessage } from "src/utils/stringUtils";

type ScimTokenModalStep = "generate" | "show-token" | "exists";

type Props = {
  open: boolean;
  onClose: () => void;
  orgId: string;
};

export const ScimTokenModal: React.FC<Props> = ({ open, onClose, orgId }) => {
  const { data: existingTokens } = useGetScimTokens(orgId);
  const hasExistingToken = (existingTokens?.length ?? 0) > 0;

  const [step, setStep] = useState<ScimTokenModalStep | null>(null);
  const [generatedToken, setGeneratedToken] = useState<string>("");
  const openRef = useRef(open);
  openRef.current = open;

  const createScimToken = useCreateScimToken(orgId);

  const currentStep = step ?? (hasExistingToken ? "exists" : "generate");

  const handleGenerate = () => {
    createScimToken.mutate(undefined, {
      onSuccess: (response) => {
        if (!openRef.current) return;
        setGeneratedToken(response.data.token);
        setStep("show-token");
      },
      onError: (error) => {
        if (!openRef.current) return;
        toastActions.failure({
          title: "Failed to generate SCIM token",
          description: errorMessage(error),
        });
      },
    });
  };

  const handleAfterLeave = () => {
    setStep(null);
    setGeneratedToken("");
    createScimToken.reset();
  };

  return (
    <Modal afterLeave={handleAfterLeave} open={open} onClose={onClose}>
      {currentStep === "generate" && (
        <GenerateStep
          loading={createScimToken.isPending}
          onGenerate={handleGenerate}
        />
      )}
      {currentStep === "show-token" && <ShowTokenStep token={generatedToken} />}
      {currentStep === "exists" && (
        <ExistsStep
          loading={createScimToken.isPending}
          onExpireAndGenerate={handleGenerate}
        />
      )}
    </Modal>
  );
};

const GenerateStep: React.FC<{
  loading: boolean;
  onGenerate: () => void;
}> = ({ loading, onGenerate }) => (
  <>
    <Modal.Header>Generate a SCIM token</Modal.Header>
    <Modal.Content>
      <p className="font-inter-normal-13px text-gray-500">
        SCIM tokens allow your identity provider to automatically provision and
        deprovision users in your organization. Once generated, use this token
        to configure the SCIM integration in your identity provider.
      </p>
    </Modal.Content>
    <Modal.Footer
      primaryButton={
        <Button
          dataLoc="scim-generate-button"
          loading={loading}
          variant="primary"
          onClick={onGenerate}
        >
          Generate
        </Button>
      }
    />
  </>
);

const ShowTokenStep: React.FC<{ token: string }> = ({ token }) => (
  <>
    <Modal.Header>Your new SCIM token</Modal.Header>
    <Modal.Content>
      <p className="mb-4 font-inter-normal-12px text-gray-500">
        Please copy your SCIM token now. You won't be able to see it again. Save
        and store it in a place, such as a password manager or secret store.
      </p>
      <div className="flex w-full items-center justify-between rounded-lg border border-gray-300 py-2 pr-3 pl-2 font-inter-normal-13px text-gray-800">
        <span className="truncate" data-loc="scim-token-value">
          {token}
        </span>
        <CopyTextIcon feedback="inline" tooltip="Copy" value={token} />
      </div>
    </Modal.Content>
    <Modal.Footer primaryButton="Done" secondaryButton={false} />
  </>
);

const ExistsStep: React.FC<{
  loading: boolean;
  onExpireAndGenerate: () => void;
}> = ({ loading, onExpireAndGenerate }) => (
  <>
    <Modal.Header>SCIM token</Modal.Header>
    <Modal.Content>
      <p className="font-inter-normal-13px text-gray-500">
        A SCIM token has already been generated for this organization.
        Generating a new token will expire the existing one.
      </p>
    </Modal.Content>
    <Modal.Footer
      primaryButton={
        <Button
          dataLoc="scim-expire-generate-button"
          loading={loading}
          variant="primary"
          onClick={onExpireAndGenerate}
        >
          Expire and generate
        </Button>
      }
    />
  </>
);
